import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import Api from '@/config/api.helper'
import { Empresa } from '@/types/plugins/session'
import { Assinatura } from '@/types/assinatura/assinatura'

interface EmpresaStore {
  empresa: Ref<Empresa | null>
  isLoading: Ref<boolean>
  hasEmpresa: Ref<boolean>
  setEmpresa(newEmpresa: Empresa | null): void
  clearEmpresa(): void
  fetchEmpresa(): Promise<Empresa | null>
  updateAssinatura(assinatura: Assinatura): void
  initialize(): Promise<void>
}

const EMPRESA_STORAGE_KEY = 'empresaStore.empresa'

function hasActiveSession(): boolean {
  const token = localStorage.getItem('token')
  return typeof token === 'string' && token.trim().length > 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeStatusDocumentacao(
  status: unknown
): Empresa['emp_status_documentacao'] {
  const allowedValues: Empresa['emp_status_documentacao'][] = [
    'PENDENTE',
    'AGUARDANDO_APROVACAO',
    'RECUSADO',
    'APROVADO',
  ]

  if (typeof status === 'string' && allowedValues.includes(status as Empresa['emp_status_documentacao'])) {
    return status as Empresa['emp_status_documentacao']
  }

  return 'PENDENTE'
}

function normalizeAssinatura(payload: unknown): Assinatura | null {
  if (!isRecord(payload)) {
    return null
  }

  const id = payload.id
  const idEmpresa = Number(payload.id_empresa)
  const idUsuario = Number(payload.id_usuario)

  if (
    (typeof id !== 'string' && typeof id !== 'number') ||
    Number.isNaN(idEmpresa) ||
    Number.isNaN(idUsuario) ||
    typeof payload.criado_em !== 'string'
  ) {
    return null
  }

  return {
    id,
    id_empresa: idEmpresa,
    id_usuario: idUsuario,
    id_cartao_empresa:
      payload.id_cartao_empresa === undefined || payload.id_cartao_empresa === null
        ? undefined
        : Number(payload.id_cartao_empresa),
    criado_em: payload.criado_em,
    atualizado_em:
      typeof payload.atualizado_em === 'string' ? payload.atualizado_em : undefined,
    deletado_em:
      typeof payload.deletado_em === 'string' || payload.deletado_em === null
        ? payload.deletado_em
        : undefined,
    status: Boolean(payload.status),
    valor:
      payload.valor === undefined || payload.valor === null
        ? undefined
        : Number(payload.valor),
    gateway: typeof payload.gateway === 'string' ? payload.gateway : undefined,
    lifetime: Boolean(payload.lifetime),
    bloqueada: Boolean(payload.bloqueada),
    bloqueada_motivo:
      typeof payload.bloqueada_motivo === 'string'
        ? payload.bloqueada_motivo
        : undefined,
    finalizada:
      typeof payload.finalizada === 'boolean' ? payload.finalizada : undefined,
    is_waiting_for_recover_plan_payment:
      typeof payload.is_waiting_for_recover_plan_payment === 'boolean'
        ? payload.is_waiting_for_recover_plan_payment
        : undefined,
    planos_servicos_counter: Array.isArray(payload.planos_servicos_counter)
      ? (payload.planos_servicos_counter as Assinatura['planos_servicos_counter'])
      : payload.planos_servicos_counter === null
        ? null
        : undefined,
  }
}

function createPlaceholderAssinatura(payload: Record<string, unknown>): Assinatura {
  const empId = Number(payload.emp_id ?? 0)
  const normalizedEmpId = Number.isNaN(empId) ? 0 : empId
  const uuidEmpId = typeof payload.uuid_emp_id === 'string' ? payload.uuid_emp_id : 'empresa'

  return {
    id: `placeholder-${uuidEmpId}`,
    id_empresa: normalizedEmpId,
    id_usuario: 0,
    criado_em: '',
    status: false,
    lifetime: false,
    bloqueada: false,
  }
}

function normalizeEmpresaPayload(payload: unknown): Empresa | null {
  if (!isRecord(payload)) {
    return null
  }

  const endereco = isRecord(payload.endereco)
    ? {
        een_latitude:
          typeof payload.endereco.een_latitude === 'string' ||
          payload.endereco.een_latitude === null
            ? payload.endereco.een_latitude
            : null,
        een_longitude:
          typeof payload.endereco.een_longitude === 'string' ||
          payload.endereco.een_longitude === null
            ? payload.endereco.een_longitude
            : null,
      }
    : {
        een_latitude: null,
        een_longitude: null,
      }

  const empresaDocumentacao = isRecord(payload.empresa_documentacao)
    ? {
        uuid_edo_id:
          typeof payload.empresa_documentacao.uuid_edo_id === 'string'
            ? payload.empresa_documentacao.uuid_edo_id
            : '',
        edo_id: Number(payload.empresa_documentacao.edo_id ?? 0),
        edo_cnpj:
          typeof payload.empresa_documentacao.edo_cnpj === 'string'
            ? payload.empresa_documentacao.edo_cnpj
            : '',
        edo_criado_em:
          typeof payload.empresa_documentacao.edo_criado_em === 'string'
            ? payload.empresa_documentacao.edo_criado_em
            : '',
        edo_contrato_principal_status: Boolean(
          payload.empresa_documentacao.edo_contrato_principal_status
        ),
        edo_contrato_principal_assinado_url:
          typeof payload.empresa_documentacao.edo_contrato_principal_assinado_url === 'string'
            ? payload.empresa_documentacao.edo_contrato_principal_assinado_url
            : null,
        edo_anexo_i_status: Boolean(payload.empresa_documentacao.edo_anexo_i_status),
        edo_anexo_i_assinado_url:
          typeof payload.empresa_documentacao.edo_anexo_i_assinado_url === 'string'
            ? payload.empresa_documentacao.edo_anexo_i_assinado_url
            : null,
      }
    : {
        uuid_edo_id: '',
        edo_id: 0,
        edo_cnpj: '',
        edo_criado_em: '',
        edo_contrato_principal_status: false,
        edo_contrato_principal_assinado_url: null,
        edo_anexo_i_status: false,
        edo_anexo_i_assinado_url: null,
      }

  // Empresa pode vir sem assinatura neste endpoint; assinatura real é sincronizada depois via assinaturaStore.
  const assinatura =
    normalizeAssinatura(payload.assinatura) ?? createPlaceholderAssinatura(payload)

  if (
    typeof payload.uuid_emp_id !== 'string' ||
    Number.isNaN(Number(payload.emp_id)) ||
    typeof payload.emp_razao_social !== 'string' ||
    Number.isNaN(Number(payload.emp_tipo))
  ) {
    return null
  }

  return {
    uuid_emp_id: payload.uuid_emp_id,
    emp_id: Number(payload.emp_id),
    emp_documentacao_status: Boolean(payload.emp_documentacao_status),
    emp_status_documentacao: normalizeStatusDocumentacao(
      payload.emp_status_documentacao
    ),
    emp_natureza_operacao: Number(payload.emp_natureza_operacao ?? 0),
    emp_primeiro_login: Boolean(payload.emp_primeiro_login),
    emp_tipo: Number(payload.emp_tipo),
    emp_razao_social: payload.emp_razao_social,
    emp_criado_em:
      typeof payload.emp_criado_em === 'string' ? payload.emp_criado_em : null,
    emp_focus_id:
      typeof payload.emp_focus_id === 'string' || payload.emp_focus_id === null
        ? payload.emp_focus_id
        : null,
    emp_bloqueada: Boolean(payload.emp_bloqueada),
    endereco,
    assinatura,
    empresa_documentacao: empresaDocumentacao,
    emp_volume_cobranca: Number(payload.emp_volume_cobranca ?? 0),
    emp_segmento: typeof payload.emp_segmento === 'string' || payload.emp_segmento === null
      ? payload.emp_segmento
      : null,
  }
}

function extractEmpresaPayload(responseData: unknown): unknown {
  if (!isRecord(responseData)) {
    return responseData
  }

  if ('empresa' in responseData) {
    return responseData.empresa
  }

  if ('data' in responseData) {
    const data = responseData.data

    if (isRecord(data) && 'empresa' in data) {
      return data.empresa
    }

    return data
  }

  return responseData
}

export const useEmpresaStore = defineStore('empresa', (): EmpresaStore => {
  const empresa = ref<Empresa | null>(null)
  const isLoading = ref<boolean>(false)

  const hasEmpresa = computed((): boolean => !!empresa.value)

  function setEmpresa(newEmpresa: Empresa | null): void {
    empresa.value = newEmpresa

    if (newEmpresa) {
      localStorage.setItem(EMPRESA_STORAGE_KEY, JSON.stringify(newEmpresa))
      return
    }

    localStorage.removeItem(EMPRESA_STORAGE_KEY)
  }

  function clearEmpresa(): void {
    setEmpresa(null)
  }

  async function fetchEmpresa(): Promise<Empresa | null> {
    if (!hasActiveSession()) {
      return null
    }

    isLoading.value = true

    try {
      const response = await Api.get<unknown>('v2/empresa')

      const payload = extractEmpresaPayload(response.data)
      const normalizedEmpresa = normalizeEmpresaPayload(payload)

      if (!normalizedEmpresa) {
        clearEmpresa()
        return null
      }

      setEmpresa(normalizedEmpresa)
      return normalizedEmpresa
    } catch (error) {
      console.error('Erro ao buscar dados da empresa:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  function updateAssinatura(assinatura: Assinatura): void {
    if (!empresa.value) {
      return
    }

    setEmpresa({
      ...empresa.value,
      assinatura,
    })
  }

  async function initialize(): Promise<void> {
    if (!hasActiveSession()) {
      return
    }

    let cachedEmpresa = localStorage.getItem(EMPRESA_STORAGE_KEY)

    if (!cachedEmpresa) {
      await fetchEmpresa()
      cachedEmpresa = localStorage.getItem(EMPRESA_STORAGE_KEY)

      if (!cachedEmpresa) {
        return
      }
    }

    try {
      const parsedEmpresa = JSON.parse(cachedEmpresa) as unknown
      const normalizedEmpresa = normalizeEmpresaPayload(parsedEmpresa)

      if (!normalizedEmpresa) {
        clearEmpresa()
        await fetchEmpresa()
        return
      }

      empresa.value = normalizedEmpresa
    } catch (error) {
      console.error('Erro ao carregar dados da empresa em cache:', error)
      clearEmpresa()
      await fetchEmpresa()
    }
  }

  return {
    empresa,
    isLoading,
    hasEmpresa,
    setEmpresa,
    clearEmpresa,
    fetchEmpresa,
    updateAssinatura,
    initialize,
  }
})